import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/categories";
import ProductCard from "@/components/ProductCard";

export default function CatalogPage() {
    const { category: routeCat } = useParams();
    const [products, setProducts] = useState([]);
    const [category, setCategory] = useState(routeCat || "");
    const [carats, setCarats] = useState("");
    const [length, setLength] = useState("");
    const [model, setModel] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setCategory(routeCat || "");
        setModel("");
        setCarats("");
        setLength("");
    }, [routeCat]);

    useEffect(() => {
        setLoading(true);
        api.get("/products")
            .then((r) => setProducts(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const facets = useMemo(() => {
        const caratsSet = new Set();
        const lengthSet = new Set();
        const modelSet = new Set();
        products
            .filter((p) => !category || p.category === category)
            .forEach((p) =>
                p.variants?.forEach((v) => {
                    if (v.carats) caratsSet.add(v.carats);
                    if (v.length_cm) lengthSet.add(v.length_cm);
                    if (v.model) modelSet.add(v.model);
                })
            );
        return {
            carats: [...caratsSet].sort(),
            lengths: [...lengthSet].sort(),
            models: [...modelSet].sort(),
        };
    }, [products, category]);

    const pickType = (cat) => {
        setCategory(cat);
        setModel("");
        setCarats("");
        setLength("");
    };

    const filtered = useMemo(() => {
        return products.filter((p) => {
            if (category && p.category !== category) return false;
            const vs = p.variants || [];
            if (carats && !vs.some((v) => v.carats === carats)) return false;
            if (length && !vs.some((v) => v.length_cm === length)) return false;
            if (model && !vs.some((v) => v.model === model)) return false;
            return true;
        });
    }, [products, category, carats, length, model]);

    const Pill = ({ active, onClick, children, tid }) => (
        <button data-testid={tid} onClick={onClick} className={`variant-pill ${active ? "active" : ""}`}>
            {children}
        </button>
    );

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
            <div className="text-[10px] uppercase tracking-[0.35em] text-slate-500 mb-3">Catalogue</div>
            <h1 className="font-serif-luxe text-3xl sm:text-5xl chrome-text font-semibold mb-10">
                {category ? CATEGORY_LABELS[category] : "Toute la collection"}
            </h1>

            <div data-testid="catalog-filters" className="mb-10 space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                    <span className="text-xs uppercase tracking-widest text-slate-500 w-20">Type</span>
                    <Pill tid="filter-type-all" active={!category} onClick={() => pickType("")}>Tout</Pill>
                    <Pill tid="filter-type-chaines" active={category === "chaines"} onClick={() => pickType("chaines")}>Chaînes</Pill>
                    <Pill tid="filter-type-pendentifs" active={category === "pendentifs"} onClick={() => pickType("pendentifs")}>Pendentifs</Pill>
                    <Pill tid="filter-type-bracelets" active={category === "bracelets"} onClick={() => pickType("bracelets")}>Bracelets</Pill>
                    <Pill tid="filter-type-boucles" active={category === "boucles"} onClick={() => pickType("boucles")}>Boucles d'oreille</Pill>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <span className="text-xs uppercase tracking-widest text-slate-500 w-20">Modèle</span>
                    <Pill tid="filter-model-all" active={!model} onClick={() => setModel("")}>Tous</Pill>
                    {facets.models.map((m) => (
                        <Pill key={m} tid={`filter-model-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} active={model === m} onClick={() => setModel(m)}>{m}</Pill>
                    ))}
                </div>
                {facets.carats.length > 0 && (
                <div className="flex flex-wrap items-center gap-3">
                    <span className="text-xs uppercase tracking-widest text-slate-500 w-20">Carats</span>
                    <Pill tid="filter-carats-all" active={!carats} onClick={() => setCarats("")}>Tous</Pill>
                    {facets.carats.map((c) => (
                        <Pill key={c} tid={`filter-carats-${c.replace(/[^a-z0-9]+/g, "-")}`} active={carats === c} onClick={() => setCarats(c)}>{c}</Pill>
                    ))}
                </div>
                )}
                <div className="flex flex-wrap items-center gap-3">
                    <span className="text-xs uppercase tracking-widest text-slate-500 w-20">Taille</span>
                    <Pill tid="filter-length-all" active={!length} onClick={() => setLength("")}>Toutes</Pill>
                    {facets.lengths.map((l) => (
                        <Pill key={l} tid={`filter-length-${l.replace(/[^a-z0-9]+/g, "-")}`} active={length === l} onClick={() => setLength(l)}>{l}</Pill>
                    ))}
                </div>
            </div>

            {loading ? (
                <div data-testid="catalog-loading" className="text-slate-500 py-20 text-center">Chargement…</div>
            ) : filtered.length === 0 ? (
                <div data-testid="catalog-empty" className="text-slate-500 py-20 text-center">
                    Aucune pièce ne correspond à ces filtres.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
            )}
        </div>
    );
}
